import { useEffect, useState } from 'react'
import { usePageData } from '@/hooks/usePageData'
import { mdToHtml } from '@/lib/markdown'
import type { AboutPageData, TeamMember } from '@/types/team'

/*
  Figma: node 260:3205 (About Us)
  - bg: #F9F9FA (colour/bg/page)
  - Page header: "About us" - h1-title (180px, alfarn-2, weight 700)
  - Mission text: alfarn-2 32px weight 700, lineHeight 1.3
  - "What we do" heading: h2-xxlarge (100px)
  - Services: two-column layout, 48px gap
  - "Meet the team": left title (100px) + right staff cards (database-driven)
  - BACK button: Poppins Bold 60px, links to homepage

  Team members are fetched from the Insites database (modules/website/our_team)
  via GraphQL in about.liquid and passed through the React mount data bridge.

  Styling lives in src/styles/pages/about.css.
*/

function Divider({ height }: { height?: 108 | 24 }) {
  if (!height) return <div className="mig-divider-line" />
  return <div className="mig-about-divider" data-h={String(height)} />
}

function StaffCard({ member }: { member: TeamMember }) {
  const imageUrl =
    member.image?.versions?.card ||
    member.image?.url ||
    null

  return (
    <div className="mig-about-staff-card">
      <div className="mig-about-staff-bio">
        <div className="mig-about-staff-person">
          <h3 className="mig-about-staff-name">{member.name}</h3>
          <p className="mig-about-staff-title">{member.role}</p>
        </div>

        {member.bio && (
          /* IIA stores the bio field as raw markdown (our_team.yml sets
             ui_element: "markdown"). Convert to HTML before injecting so
             **bold**, *italic*, [links](url), paragraphs and line breaks
             render as the admin intended instead of leaking through as
             literal markdown source. */
          <div
            className="mig-about-body-text mig-about-bio-content"
            dangerouslySetInnerHTML={{ __html: mdToHtml(member.bio) }}
          />
        )}
      </div>

      {/* Only render the headshot container when the member has an
          image — no grey placeholder for members without a photo. The
          bio column (flex: 1) fills the row when the headshot is absent. */}
      {imageUrl && (
        <div className="mig-about-headshot">
          <img
            src={imageUrl}
            alt={member.name}
            loading="lazy"
            decoding="async"
          />
        </div>
      )}
    </div>
  )
}

export default function About() {
  const [visible, setVisible] = useState(false)
  const data = usePageData<AboutPageData>()
  const teamMembers: TeamMember[] = data?.team_members ?? []

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100)
    return () => clearTimeout(timer)
  }, [])

  return (
    <section className="mig-page-section">
      <div className="mig-page-title-row" data-visible={visible}>
        <h1 className="mig-about-page-title">About us</h1>
      </div>

      <div className="mig-about-content">
        {/* Mission text */}
        <div className="mig-about-mission-text">
          <p>
            Migration Galleries is built on the idea of creating new opportunities
            by moving art. We bring quality artworks from Sydney's established
            art hubs into vibrant social spaces in the suburbs where people
            gather, connect and spend time.
          </p>
          <p>
            For people living outside traditional gallery areas, visiting a gallery
            is not always easy and may be overlooked as an enriching activity.
            Migration Galleries exists to change that. Our aim is to bring quality
            artworks to new audiences, making art more accessible, more visible,
            and more integrated into everyday life.
          </p>
          <p>
            Our online art marketplace champions this mission by putting artists
            and their works front and centre. We showcase a diverse range of
            quality artworks and encourage the use of both new works and
            stockpiled pieces, across varied styles and genres.
            We believe that art is an experience that gives sincere value to
            humankind — Migration Galleries seeks to create many more of
            these experiences.
          </p>
        </div>

        {/* === What we do section === */}
        <div className="mig-about-section-group">
          <Divider />
          <h2 className="mig-about-section-heading">What we do</h2>

          <div className="mig-about-services-grid">
            {/* Left column */}
            <div className="mig-about-services-col">
              <div className="mig-about-service-stack">
                <h3 className="mig-about-service-title">Online sales</h3>
                <p className="mig-about-body-text">
                  Migration Galleries recognises the challenge of finding and selling art on a professional and well-produced website. Therefore, our website serves as a central platform where both artists and buyers can benefit: artists can feel confident that they are presenting their best artworks and buyers can experience comfort when discovering and purchasing premium quality art. Alongside handling sales transactions, our website additionally offers show calendars and feature articles. We bring consumers and producers together so that they can both achieve desired results.
                </p>
              </div>

              <div className="mig-about-image-block">
                <img
                  src="/assets/modules/website/images/about-us/what-we-do-first.webp"
                  alt="Set dining tables with mustard-yellow velvet chairs in a bright, modern restaurant interior"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>

            {/* Right column */}
            <div className="mig-about-services-col">
              <div className="mig-about-image-block">
                <img
                  src="/assets/modules/website/images/about-us/what-we-do-second.webp"
                  alt="A lively inner-city street with heritage shopfronts, plane trees, rainbow-painted facades, pedestrians and passing cars"
                  loading="lazy"
                  decoding="async"
                />
              </div>

              <div className="mig-about-service-stack">
                <h3 className="mig-about-service-title">Exhibitions</h3>
                <div className="mig-about-body-text">
                  <p>
                    At Migration Galleries, we hold curated exhibitions of artworks at our selected sites — we help take art beyond traditional art hubs and into suburban venues. Through this, we dismantle the barriers to accessing art and integrate it into everyday lives. This way, art can be felt and shared on a vaster scale. We want to reiterate the connections between people and art, and between people.
                  </p>
                  <p>
                    With thoroughly vetted works and artists, we assure that our pop-up exhibitions display the finest works while uplifting our local artists.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* === Meet the team section === */}
        <div className="mig-about-team-section">
          <h2 className="mig-about-section-heading mig-about-team-title">
            Meet the team
          </h2>

          <div className="mig-about-team-cards">
            {teamMembers.map((member, index) => (
              <div key={member.id} className="mig-about-team-card-wrapper">
                <div className="mig-about-team-divider">
                  <Divider height={index === 0 ? 108 : 24} />
                </div>
                <StaffCard member={member} />
              </div>
            ))}
          </div>
        </div>

        {/* BACK button */}
        <div className="mig-about-back-row">
          <a href="/" className="mig-about-back-button">BACK</a>
          <Divider />
        </div>
      </div>
    </section>
  )
}
